import { connectorsForWallets, Theme, darkTheme, RainbowKitProvider } from '@rainbow-me/rainbowkit';
import { coinbaseWallet, injectedWallet, metaMaskWallet, walletConnectWallet } from '@rainbow-me/rainbowkit/wallets';
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import "./assets/styles/styleguide.css";
import './assets/styles/helpers.scss';
import './assets/styles/typography.scss';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import deepBlue from './assets/styles/deep-blue';
import deepSeaBlue from './assets/styles/deep-sea-blue';
import mainGreen from './assets/styles/main-green';
import { BreakpointProvider } from './context-providers/BreakpointProvider';
import ContractsProvider from './context-providers/ContractsProvider';
import { ModalProvider } from './context-providers/ModalProvider';
import { ServicesProvider } from './context-providers/ServicesProvider';
import { ToasterProvider } from './context-providers/ToasterProvider/ToasterProvider';
import leverageUiColors from './helpers/config/colors';
import reportWebVitals from './reportWebVitals';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import '@rainbow-me/rainbowkit/styles.css';
import { merge } from 'lodash';
import { PaletteColorOptions } from "@mui/material";
import { createConfig, WagmiProvider, http } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {arbitrum, optimism, base, mainnet, avalanche, bsc, zkSync, polygonZkEvm, polygon} from "viem/chains";

declare module '@mui/material/styles' {
    interface CustomPalette {
        orange: PaletteColorOptions;
    }

    interface Palette extends CustomPalette {
    }

    interface PaletteOptions extends CustomPalette {
    }
}

declare module '@mui/material/Button' {
    interface ButtonPropsColorOverrides {
        orange: true;
    }
}
declare module '@mui/material/Slider' {
    interface SliderPropsColorOverrides {
        orange: true;
    }
}

const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
);

const client = new QueryClient();

const rainbowKitTheme: Theme = merge(darkTheme(), {
    radii: {
        modal: '4px',
        modalMobile: '4px'
    },
    fonts: {
        body: 'var(--font-family-regular)'
    },
    colors: {
        accentColor: leverageUiColors.main,
        accentColorForeground: 'rgba(0,0,0,0.87)',
        closeButton: leverageUiColors.neutralWhite,
        closeButtonBackground: 'transparent',
        modalBackground: leverageUiColors.bg2,
        profileForeground: leverageUiColors.bg2
    }
});

const { palette } = createTheme();
const { augmentColor } = palette;
const createColor = (mainColor: string) => augmentColor({ color: { main: mainColor } });

const theme = createTheme({
    typography: {
        fontFamily: 'var(--font-family-regular)'
    },
    palette: {
        mode: 'dark',
        primary: mainGreen,
        secondary: deepBlue,
        orange: createColor(leverageUiColors.statusWarning),

        background: {
            default: deepSeaBlue[500],
            paper: deepSeaBlue[500]
        }
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    '&.Mui-disabled.MuiButton-containedPrimary': {
                        backgroundColor: leverageUiColors.main,
                        opacity: 0.6,
                        color: 'rgba(0,0,0,0.87)'
                    }
                }
            }
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    // nereus-button-l
                    textTransform: 'none',
                    fontFamily: 'var(--font-family-regular)',
                    fontSize: 'var(--font-size-base)',
                    fontWeight: 'var(--font-weight-regular)',
                    lineHeight: 'var(--line-height-base)'
                }
            }
        },
        MuiAlert: {
            styleOverrides: {
                standardError: {
                    backgroundColor: leverageUiColors.statusDown10,
                    color: leverageUiColors.statusDown,
                    border: `1px solid ${leverageUiColors.statusDown}`
                },
                standardInfo: {
                    backgroundColor: leverageUiColors.bg2,
                    color: leverageUiColors.neutralWhite,
                    border: `1px solid ${leverageUiColors.neutralGrey}`,
                    '& .MuiAlert-icon': {
                        color: leverageUiColors.neutralGrey
                    }
                }
            }
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    backgroundColor: leverageUiColors.bg1,
                    borderRadius: '4px',
                    border: `1px solid ${leverageUiColors.neutralDarkBlue}`
                }
            }
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    backgroundColor: leverageUiColors.bg2,
                    backgroundImage: 'none',
                    borderRadius: '4px',
                    border: `1px solid ${leverageUiColors.neutralDarkBlue}`
                }
            }
        },
        MuiSlider: {
            styleOverrides: {
                valueLabel: {
                    backgroundColor: leverageUiColors.main,
                    color: leverageUiColors.bg1
                }
            }
        }
    }
});


const connectors = connectorsForWallets(
    [
        {
            groupName: 'Recommended',
            wallets: [injectedWallet, metaMaskWallet, walletConnectWallet, coinbaseWallet],
        },
    ],
    {
        appName: 'Nereus Leverage',
        projectId: process.env?.REACT_APP_WALLET_CONNECT_PROJECT_ID || ''
    }
);


const config = createConfig({
    connectors,
    chains: [base, mainnet, arbitrum, optimism, avalanche, bsc, zkSync, polygonZkEvm, polygon],
    transports: {
        [base.id]: http(process.env.REACT_APP_BASE_RPC),
        [mainnet.id]: http(mainnet.rpcUrls.default.http[0]),
        [arbitrum.id]: http(arbitrum.rpcUrls.default.http[0]),
        [optimism.id]: http(optimism.rpcUrls.default.http[0]),
        [avalanche.id]: http(avalanche.rpcUrls.default.http[0]),
        [bsc.id]: http(bsc.rpcUrls.default.http[0]),
        [zkSync.id]: http(zkSync.rpcUrls.default.http[0]),
        [polygonZkEvm.id]: http(polygonZkEvm.rpcUrls.default.http[0]),
        [polygon.id]: http(polygon.rpcUrls.default.http[0]),
    }
});


root.render(
    <ThemeProvider theme={theme}>

        <WagmiProvider config={config}>
            <BreakpointProvider>
                <QueryClientProvider client={client}>
                <RainbowKitProvider modalSize="compact" theme={rainbowKitTheme}>

                    <ContractsProvider>
                        <BrowserRouter>
                            <ToasterProvider>
                                <ModalProvider>
                                    <ServicesProvider>
                                        <App/>
                                    </ServicesProvider>
                                </ModalProvider>
                            </ToasterProvider>
                        </BrowserRouter>
                    </ContractsProvider>

                </RainbowKitProvider>
                </QueryClientProvider>
            </BreakpointProvider>
        </WagmiProvider>
    </ThemeProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
